import { Esri } from '@/esriMap'
import { arcgisToGeoJSON, geojsonToArcGIS } from "@esri/arcgis-to-geojson-utils";
import { Guid } from 'guid-typescript';
import { store } from '@/store';


const featureLayers: Array<{ url: string, template: any }> = [
  {
    url: 'https://services-eu1.arcgis.com/VUxojzcglbceQdqR/arcgis/rest/services/Siti_Natura2000/FeatureServer/0',
    template: {
      title: "Siti Natura 2000: {SITENAME}",
      content: [
        {
          type: "fields",
          fieldInfos: [
            { fieldName: "SITECODE", label: "Codice sito (EAA)" },
            { fieldName: "SITENAME", label: "Nome sito" },
            { fieldName: "RELEASE_DA", label: "Data di rilascio" },
            { fieldName: "SITETYPE", label: "Tipo sito (EAA)" },
            { fieldName: "ZSC_SIC", label: "ZSC/SIC" },
            { fieldName: "ZPS", label: "ZPS" },
          ]
        }
      ]
    }
  },
  {
    url: 'https://services-eu1.arcgis.com/VUxojzcglbceQdqR/arcgis/rest/services/Territori_di_protezione_esterna_ai_parchi_(aree_contigue)/FeatureServer/0',
    template: {
      title: "Territori di protezione esterna ai parchi",
      content: [
        {
          type: "fields",
          fieldInfos: [
            { fieldName: "DESCR_TIPO ", label: "Descrizione" },
          ]
        }
      ]
    }
  },
  {
    url: 'https://services-eu1.arcgis.com/VUxojzcglbceQdqR/arcgis/rest/services/Parchi/FeatureServer/0',
    template: {
      title: "{NOME}",
      content: [
        {
          type: "fields",
          fieldInfos: [
            { fieldName: "NOME", label: "Nome" },
            { fieldName: "DESCR_TIPO", label: "Descrizione tipologia" },
            { fieldName: "NORME", label: "Normativa" },
          ]
        }
      ]
    }
  },
  {
    url: 'https://services-eu1.arcgis.com/VUxojzcglbceQdqR/arcgis/rest/services/Foreste_e_boschi/FeatureServer/0',
    template: {
      title: "Foreste e boschi",
      content: [
        {
          type: "fields",
          fieldInfos: [
            { fieldName: "TIP_FOR1", label: "Tipo di foresta" },
            { fieldName: "GOVERNO", label: "Governo" },
            { fieldName: "FONTE_DATI", label: "Fonte dati" },
          ]
        }
      ]
    }
  },
  {
    url: 'https://services-eu1.arcgis.com/VUxojzcglbceQdqR/arcgis/rest/services/Catasto_Terreni_Umbraflor/FeatureServer/0',
    template: {
      title: "Catasto terreni Umbraflor {IDENTIFICA}",
      content: [
        {
          type: "fields",
          fieldInfos: [
            { fieldName: "COMUNE", label: "Comune" },
            { fieldName: "FOGLIO", label: "Foglio" },
            { fieldName: "PARTICELLA", label: "Particella" },
            { fieldName: "NOM_COMUNE", label: "Nome Comune" },
            { fieldName: "IDENTIFICA", label: "Identifica" },
            { fieldName: "Date", label: "Data" },
            { fieldName: "Foglio_n", label: "Numero foglio" },
          ]
        }
      ]
    }
  },
  {
    url: 'https://services-eu1.arcgis.com/VUxojzcglbceQdqR/arcgis/rest/services/Confini_comunali/FeatureServer/0',
    template: {
      title: "Confine comune: {PRO_COM_T}",
      content: [
        {
          type: "fields",
          fieldInfos: [
            { fieldName: "COD_REG", label: "Regione" },
            { fieldName: "COD_PROV", label: "Provincia" },
            { fieldName: "COMUNE", label: "Comune" },
            { fieldName: "PRO_COM_T", label: "Pro/Com" },
          ]
        }
      ]
    }
  },
]

const MapServerImageLayersUrl: Array<string> = [
  'https://siat.regione.umbria.it/arcgis/rest/services/public/CATASTO_5K_WGS84_UTM/MapServer'
]

export async function addFeatureLayers(map: __esri.Map)
{

  for (const fl of featureLayers)
  {
    var layer = await Esri.Layers.FeatureLayer({
      url: fl.url,
      visible: false,
      popupEnabled: true,
      popupTemplate: fl.template
    } as __esri.FeatureLayerProperties)

    try
    {
      await layer.load();
      map.add(layer);
    } catch (e)
    {
      console.log(e);
      return null;
    }
  }

}

export async function addMapImageLayer(map: __esri.Map)
{
  for (const url of MapServerImageLayersUrl)
  {

    var layer = await Esri.Layers.MapImageLayer({
      url: url,
      visible: false
    } as __esri.MapImageLayer);

    try
    {
      await layer.load();
      map.add(layer);
    } catch (e)
    {
      console.log(e);
      return null;
    }
  }
}

export async function addExistingGraphics(map: __esri.Map, geometryType: string,
  title: string,
  fields: Array<{ nome: string; geoJSON: any; }>, visible: boolean = true): Promise<__esri.Graphic[]>
{

  let _layer: __esri.FeatureLayer = null;
  const _fields: __esri.FieldProperties[] = [
    { alias: "OBJECTID", name: "OBJECTID", nullable: false, type: "oid" },
    { alias: "Nome", name: "nome", nullable: false, type: "string" },
  ];

  const graphics = geometryType === "point" ? await makePointGraphic() : await makePolygonGraphic();
  const render = geometryType === "point" ? await makeSimpleMarkerRenderer() : await makeSimpleFillRenderer();

  const _layerId = Guid.create().toString();
  _layer = await Esri.Layers.FeatureLayer({
    fields: _fields,
    objectIdField: "OBJECTID",
    geometryType: geometryType,
    id: _layerId,
    popupEnabled: true,
    popupTemplate: { title: "{nome}" },
    source: graphics,
    renderer: render,
    title: title,
    visibile: visible,
    legendEnabled: true,
  } as __esri.FeatureLayerProperties);

  map.add(_layer, 0);
  return graphics;

  async function makePolygonGraphic(): Promise<__esri.Graphic[]>
  {
    const polygonConstructor: __esri.PolygonConstructor = await Esri.Geometry.PolygonConstructor();
    const geometryUtils = await Esri.Geometry.Support.webMercatorUtils();
    const graphics = fields.map(async (f, index) =>
    {
      const geometry = geometryUtils.geographicToWebMercator(await Esri.Geometry.Polygon(new polygonConstructor(geojsonToArcGIS(f.geoJSON))));
      const attributes = {
        OBJECTID: index,
        nome: f.nome
      };
      return Esri.Graphic({ attributes, geometry });
    });
    return Promise.all(graphics);
  }

  async function makePointGraphic(): Promise<__esri.Graphic[]>
  {
    const pointConstructor: __esri.PointConstructor = await Esri.Geometry.PointConstructor();
    const geometryUtils = await Esri.Geometry.Support.webMercatorUtils();
    const graphics = fields.map(async (f, index) =>
    {
      const geometry = geometryUtils.geographicToWebMercator(await Esri.Geometry.Point(new pointConstructor(geojsonToArcGIS(f.geoJSON))));
      const attributes = {
        OBJECTID: index,
        nome: f.nome
      };
      return Esri.Graphic({ attributes, geometry });
    });
    return Promise.all(graphics);
  }

  async function makeSimpleFillRenderer(): Promise<__esri.renderers.SimpleRenderer>
  {
    return Esri.Renderers.SimpleRenderer({
      symbol: {
        type: "simple-fill",
        color: [3, 169, 244, 0.8],
        style: "backward-diagonal",
        outline: {
          width: 1,
          color: [3, 169, 244, 1],
        },
      }
    });
  }

  async function makeSimpleMarkerRenderer(): Promise<__esri.renderers.SimpleRenderer>
  {
    return Esri.Renderers.SimpleRenderer({
      symbol: {
        type: "simple-marker", // autocasts as new SimpleMarkerSymbol()
        style: "circle",
        size: "15px", // pixels
        color: [3, 169, 244, 0.8],
        outline: {  // autocasts as new SimpleLineSymbol()
          color: "white",
          width: 1
        }
      }
    });
  }
}

export async function addUserPositionGraphic(mapView: __esri.MapView)
{

  let positionSymbol = {
    color: "white",
    height: "30px",
    type: "picture-marker",
    url: "img/user-pin.png",
    width: "30px",
    yoffset: 12,
  }

  let userPosition = await store.getters.ui.currentGPSPosition();
  if (userPosition)
  {
    let pointConstructor: __esri.PointConstructor = await Esri.Geometry.PointConstructor();
    let positionGraphic = await Esri.Graphic({
      geometry: new pointConstructor({ latitude: userPosition.latitude, longitude: userPosition.longitude }),
      symbol: positionSymbol
    });
    mapView.graphics.add(positionGraphic);
  }
}